import { type IntegrationProvider, needsNoSecret, PROVIDER_SCOPES } from "@rally/agreed";
import {
  Alert,
  Button,
  Card,
  Chip,
  Col,
  Icon,
  Input,
  Row,
  Stack,
  Tappable,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { useState } from "react";

import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";
import { ServiceMark } from "./integration-panel.tsx";
import { providerName, scopeWord } from "./integration-said.ts";

const A_SECOND_FIELD: Partial<
  Record<string, "integrations.secretWhatsapp.phone" | "integrations.secretProton.user">
> = {
  whatsapp: "integrations.secretWhatsapp.phone",
  proton: "integrations.secretProton.user",
};

export const OFFERED: IntegrationProvider[] = [
  "google",
  "proton",
  "whatsapp",
  "telegram",
  "slack",
  "youtube",
];

function SecretField({
  label,
  placeholder,
  said,
  onSaid,
}: {
  label: string;
  placeholder: string;
  said: string;
  onSaid: (next: string) => void;
}) {
  const theme = useTheme();
  return (
    <Col testID={`integration-secret-${label}`} style={{ gap: theme.space[1] }}>
      <Text typeName="caption" color="text.tertiary">
        {label}
      </Text>
      <Input
        accessibilityLabel={label}
        value={said}
        onChangeText={onSaid}
        placeholder={placeholder}
        placeholderTextColor={theme.color["text.tertiary"]}
        secureTextEntry
        autoCapitalize="none"
        autoComplete="off"
        style={{
          borderRadius: theme.radius.md,
          borderWidth: 1,
          borderColor: theme.color["border.default"],
          paddingHorizontal: theme.space[3],
          paddingVertical: theme.space[2],
          color: theme.color["text.primary"],
        }}
      />
    </Col>
  );
}

function ProviderTile({
  provider,
  chosen,
  onPick,
}: {
  provider: IntegrationProvider;
  chosen: boolean;
  onPick: () => void;
}) {
  const theme = useTheme();
  const scopes = PROVIDER_SCOPES[provider] ?? [];
  return (
    <Tappable
      accessibilityRole="button"
      accessibilityState={{ selected: chosen }}
      accessibilityLabel={providerName(provider)}
      testID={`integration-provider-${provider}`}
      onPress={onPick}
      round="lg"
      style={{
        flex: 1,
        minWidth: 180,
        gap: theme.space[2],
        padding: theme.space[3],
        borderRadius: theme.radius.lg,
        borderWidth: 1,
        borderColor: chosen ? theme.color.accent : theme.color["border.default"],
        backgroundColor: chosen ? theme.color.accentSoft : theme.color["bg.surface"],
      }}
    >
      <Row gap={2} align="center">
        <ServiceMark provider={provider} size={18} />
        <Text typeName="label" style={{ flex: 1, minWidth: 0 }} numberOfLines={1}>
          {providerName(provider)}
        </Text>
        {chosen ? <Icon name="check" size={16} color="accent" /> : null}
      </Row>
      <Row gap={1} wrap>
        {scopes.map((scope) => (
          <Chip key={scope} size="sm" muted>
            {scopeWord(scope)}
          </Chip>
        ))}
      </Row>
    </Tappable>
  );
}

export function ConnectForm({
  onDone,
  first = null,
}: {
  onDone: () => void;
  first?: IntegrationProvider | null;
}) {
  const [provider, setProvider] = useState<IntegrationProvider | null>(first);
  const [token, setToken] = useState("");
  const [second, setSecond] = useState("");
  const [wrong, setWrong] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const secondLabel = provider ? A_SECOND_FIELD[provider] : undefined;
  const keyless = provider !== null && needsNoSecret(provider);
  const ready = provider !== null && (keyless || (token.length > 0 && (!secondLabel || second.length > 0)));
  const how =
    provider === "telegram" || provider === "slack" || provider === "youtube"
      ? say(`integrations.secretHow.${provider}`)
      : null;

  const connectIt = async () => {
    if (!provider) return;
    const fields = PROVIDER_SCOPES[provider];
    setBusy(true);
    try {
      await act("integration.connect", {
        provider,
        label: providerName(provider),
        scopes: fields.map((scope) => ({ scope })),
        secret: keyless
          ? {}
          : {
              token,
              ...(secondLabel && provider === "proton" ? { userId: second } : {}),
              ...(secondLabel && provider === "whatsapp" ? { phoneNumberId: second } : {}),
            },
      });
      setToken("");
      setSecond("");
      setProvider(null);
      onDone();
    } catch (cause: unknown) {
      setWrong(say("integrations.connectFailed"));
      if (!(cause instanceof Error)) return;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="inset" testID="integration-connect-form">
      <Stack gap={4}>
        <Stack gap={2}>
          <Text typeName="label">{say("integrations.connect")}</Text>
          <Text typeName="caption" color="text.tertiary">
            {say("integrations.hint")}
          </Text>
        </Stack>

        <Row gap={2} wrap align="stretch">
          {OFFERED.map((one) => (
            <ProviderTile
              key={one}
              provider={one}
              chosen={provider === one}
              onPick={() => setProvider(provider === one ? null : one)}
            />
          ))}
        </Row>

        {provider ? (
          <Stack gap={3}>
            {how ? (
              <Text typeName="caption" color="text.secondary" testID="integration-secret-how">
                {how}
              </Text>
            ) : null}
            {keyless ? null : (
              <SecretField
                label={say("integrations.secret.prompt")}
                placeholder={say("integrations.secret.placeholder")}
                said={token}
                onSaid={setToken}
              />
            )}
            {secondLabel ? (
              <SecretField
                label={say(secondLabel)}
                placeholder={
                  secondLabel === "integrations.secretProton.user"
                    ? say("integrations.secretProton.user")
                    : say("integrations.secretWhatsapp.phone")
                }
                said={second}
                onSaid={setSecond}
              />
            ) : null}
            {keyless ? null : (
              <Row gap={2} align="center">
                <Icon name="lock" size={13} color="text.tertiary" />
                <Text typeName="caption" color="text.tertiary" style={{ flex: 1, minWidth: 0 }}>
                  {say("integrations.sealed")}
                </Text>
              </Row>
            )}
          </Stack>
        ) : null}

        {wrong ? <Alert tone="danger">{wrong}</Alert> : null}

        <Row gap={2}>
          <Button
            disabled={!ready}
            loading={busy}
            icon="link"
            testID="integration-connect-press"
            onPress={() => void connectIt()}
          >
            {say("integrations.connect")}
          </Button>
          <Button variant="ghost" testID="integration-connect-dismiss" onPress={onDone}>
            {say("integrations.dismiss")}
          </Button>
        </Row>
      </Stack>
    </Card>
  );
}
